/**
 * Peptide Labs — Shopify OS 2.0 Theme JavaScript
 * Vanilla JS, custom elements, no jQuery.
 */
(function () {
  'use strict';

  /* ========================================================================
   * Helpers
   * ======================================================================== */

  function debounce(fn, delay) {
    let timer;
    return function (...args) {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), delay);
    };
  }

  function fetchJSON(url, options) {
    return fetch(url, options).then(function (res) {
      if (!res.ok) throw new Error(res.statusText);
      return res.json();
    });
  }

  function trapFocus(container) {
    const focusable = container.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    function handler(e) {
      if (e.key !== 'Tab') return;
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    }
    container.addEventListener('keydown', handler);
    container._trapFocusHandler = handler;
    first.focus();
  }

  function releaseFocus(container) {
    if (container._trapFocusHandler) {
      container.removeEventListener('keydown', container._trapFocusHandler);
      delete container._trapFocusHandler;
    }
  }

  /* ========================================================================
   * 1. AnnouncementBar
   * ======================================================================== */

  const AnnouncementBar = {
    _interval: null,
    _paused: false,

    init: function () {
      const wrapper = document.querySelector('[data-announcement-bar]');
      if (!wrapper) return;

      // Restore dismissed state
      if (sessionStorage.getItem('peptide-labs-announcement-hidden') === 'true') {
        const sectionWrapper = wrapper.closest('.shopify-section') || wrapper;
        sectionWrapper.style.display = 'none';
        return;
      }

      // Close button
      const closeBtn = wrapper.querySelector('[data-announcement-close]');
      if (closeBtn) {
        closeBtn.addEventListener('click', function () {
          const sectionWrapper = wrapper.closest('.shopify-section') || wrapper;
          sectionWrapper.style.display = 'none';
          sessionStorage.setItem('peptide-labs-announcement-hidden', 'true');
        });
      }

      // Message rotation
      const messages = wrapper.querySelectorAll('[data-announcement-message]');
      if (messages.length <= 1) return;

      let current = 0;
      messages.forEach(function (msg, i) {
        msg.style.display = i === 0 ? '' : 'none';
      });

      function show(index) {
        messages[current].style.display = 'none';
        current = (index + messages.length) % messages.length;
        messages[current].style.display = '';
      }

      const prevBtn = wrapper.querySelector('[data-announcement-prev]');
      const nextBtn = wrapper.querySelector('[data-announcement-next]');
      if (prevBtn) prevBtn.addEventListener('click', function () { show(current - 1); });
      if (nextBtn) nextBtn.addEventListener('click', function () { show(current + 1); });

      // Auto-rotation
      const timer = parseInt(wrapper.dataset.autoTimer, 10) || 5000;
      function startAuto() {
        AnnouncementBar._interval = setInterval(function () {
          if (!AnnouncementBar._paused) show(current + 1);
        }, timer);
      }
      startAuto();

      wrapper.addEventListener('mouseenter', function () { AnnouncementBar._paused = true; });
      wrapper.addEventListener('mouseleave', function () { AnnouncementBar._paused = false; });

      // Touch / swipe
      let touchStartX = 0;
      wrapper.addEventListener('touchstart', function (e) {
        touchStartX = e.changedTouches[0].clientX;
      }, { passive: true });
      wrapper.addEventListener('touchend', function (e) {
        const dx = e.changedTouches[0].clientX - touchStartX;
        if (Math.abs(dx) > 40) show(dx > 0 ? current - 1 : current + 1);
      }, { passive: true });
    },

    destroy: function () {
      clearInterval(AnnouncementBar._interval);
    }
  };

  /* ========================================================================
   * 2. StickyHeader
   * ======================================================================== */

  const StickyHeader = {
    _handler: null,

    init: function () {
      const header = document.querySelector('.section-site-header');
      if (!header) return;

      StickyHeader._handler = function () {
        if (window.scrollY > 50) {
          header.classList.add('is-scrolled');
        } else {
          header.classList.remove('is-scrolled');
        }
      };

      window.addEventListener('scroll', StickyHeader._handler, { passive: true });
      StickyHeader._handler();
    },

    destroy: function () {
      if (StickyHeader._handler) {
        window.removeEventListener('scroll', StickyHeader._handler);
      }
    }
  };

  /* ========================================================================
   * 3. MobileMenu
   * ======================================================================== */

  const MobileMenu = {
    _drawer: null,
    _overlay: null,
    _toggleBtn: null,
    _escHandler: null,

    init: function () {
      const toggle = document.querySelector('[data-mobile-menu-toggle]');
      const drawer = document.querySelector('[data-mobile-menu-drawer]');
      const overlay = document.querySelector('[data-mobile-menu-overlay]');
      if (!toggle || !drawer) return;

      MobileMenu._drawer = drawer;
      MobileMenu._overlay = overlay;
      MobileMenu._toggleBtn = toggle;

      toggle.addEventListener('click', function () {
        if (drawer.classList.contains('is-open')) {
          MobileMenu.close();
        } else {
          MobileMenu.open();
        }
      });

      if (overlay) overlay.addEventListener('click', function () { MobileMenu.close(); });

      MobileMenu._escHandler = function (e) {
        if (e.key === 'Escape') MobileMenu.close();
      };

      // Accordion sub-menus
      drawer.querySelectorAll('[data-mobile-submenu-toggle]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          const submenu = btn.nextElementSibling;
          if (!submenu) return;
          const isOpen = btn.getAttribute('aria-expanded') === 'true';
          btn.setAttribute('aria-expanded', String(!isOpen));
          submenu.style.maxHeight = isOpen ? '0px' : submenu.scrollHeight + 'px';
        });
      });
    },

    open: function () {
      var drawer = MobileMenu._drawer;
      var overlay = MobileMenu._overlay;
      if (!drawer) return;
      drawer.classList.add('is-open');
      if (overlay) overlay.classList.add('is-visible');
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', MobileMenu._escHandler);
      trapFocus(drawer);
      if (MobileMenu._toggleBtn) MobileMenu._toggleBtn.setAttribute('aria-expanded', 'true');
    },

    close: function () {
      var drawer = MobileMenu._drawer;
      var overlay = MobileMenu._overlay;
      if (!drawer) return;
      drawer.classList.remove('is-open');
      if (overlay) overlay.classList.remove('is-visible');
      document.body.style.overflow = '';
      document.removeEventListener('keydown', MobileMenu._escHandler);
      releaseFocus(drawer);
      if (MobileMenu._toggleBtn) {
        MobileMenu._toggleBtn.setAttribute('aria-expanded', 'false');
        MobileMenu._toggleBtn.focus();
      }
    },

    destroy: function () {
      MobileMenu.close();
    }
  };

  /* ========================================================================
   * 4. PredictiveSearch
   * ======================================================================== */

  const PredictiveSearch = {
    init: function () {
      document.querySelectorAll('[data-predictive-search]').forEach(function (form) {
        const input = form.querySelector('[data-search-input]');
        const results = form.querySelector('[data-search-results]');
        if (!input || !results) return;

        let activeIndex = -1;

        const fetchResults = debounce(function (query) {
          if (query.length < 2) { results.innerHTML = ''; results.hidden = true; return; }
          fetch(
            window.Shopify && window.Shopify.routes
              ? window.Shopify.routes.root + 'search/suggest.json?q=' + encodeURIComponent(query) + '&resources[type]=product,page,article&resources[limit]=6'
              : '/search/suggest.json?q=' + encodeURIComponent(query) + '&resources[type]=product,page,article&resources[limit]=6'
          )
            .then(function (r) { return r.json(); })
            .then(function (data) {
              activeIndex = -1;
              var resources = data.resources && data.resources.results;
              if (!resources) { results.innerHTML = ''; results.hidden = true; return; }
              var html = '';
              ['products', 'pages', 'articles'].forEach(function (type) {
                if (!resources[type] || !resources[type].length) return;
                html += '<div class="predictive-search__group"><h4 class="predictive-search__heading">' + type.charAt(0).toUpperCase() + type.slice(1) + '</h4><ul>';
                resources[type].forEach(function (item) {
                  var image = item.image || (item.featured_image && item.featured_image.url) || '';
                  html += '<li class="predictive-search__item"><a href="' + item.url + '">';
                  if (image) html += '<img src="' + image + '" alt="" width="40" height="40" loading="lazy">';
                  html += '<span>' + item.title + '</span></a></li>';
                });
                html += '</ul></div>';
              });
              results.innerHTML = html || '<p class="predictive-search__empty">No results found</p>';
              results.hidden = false;
            })
            .catch(function () { results.innerHTML = ''; results.hidden = true; });
        }, 300);

        input.addEventListener('input', function () { fetchResults(input.value.trim()); });

        input.addEventListener('keydown', function (e) {
          var items = results.querySelectorAll('.predictive-search__item a');
          if (!items.length) return;
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            activeIndex = (activeIndex + 1) % items.length;
            items[activeIndex].focus();
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            activeIndex = (activeIndex - 1 + items.length) % items.length;
            items[activeIndex].focus();
          } else if (e.key === 'Escape') {
            results.innerHTML = '';
            results.hidden = true;
            input.focus();
          }
        });

        results.addEventListener('keydown', function (e) {
          var items = results.querySelectorAll('.predictive-search__item a');
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            activeIndex = (activeIndex + 1) % items.length;
            items[activeIndex].focus();
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            activeIndex = (activeIndex - 1 + items.length) % items.length;
            items[activeIndex].focus();
          } else if (e.key === 'Escape') {
            results.innerHTML = '';
            results.hidden = true;
            input.focus();
          }
        });

        // Close on outside click
        document.addEventListener('click', function (e) {
          if (!form.contains(e.target)) { results.innerHTML = ''; results.hidden = true; }
        });
      });
    }
  };

  /* ========================================================================
   * 5. QuantitySelector
   * ======================================================================== */

  const QuantitySelector = {
    init: function () {
      document.querySelectorAll('[data-quantity-selector]').forEach(function (el) {
        const input = el.querySelector('[data-quantity-input]');
        const minus = el.querySelector('[data-quantity-minus]');
        const plus = el.querySelector('[data-quantity-plus]');
        if (!input) return;

        var min = parseInt(input.min, 10) || 1;
        var max = parseInt(input.max, 10) || parseInt(input.dataset.max, 10) || 99;

        function update(val) {
          val = Math.max(min, Math.min(max, val));
          input.value = val;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }

        if (minus) minus.addEventListener('click', function () { update(parseInt(input.value, 10) - 1); });
        if (plus) plus.addEventListener('click', function () { update(parseInt(input.value, 10) + 1); });
        input.addEventListener('change', function () { update(parseInt(input.value, 10) || min); });
      });
    }
  };

  /* ========================================================================
   * 6. ProductForm
   * ======================================================================== */

  const ProductForm = {
    init: function () {
      document.querySelectorAll('[data-product-form]').forEach(function (form) {
        var productData;
        var jsonEl = form.closest('[data-product-json]') || document.querySelector('[data-product-json]');
        if (jsonEl) {
          try { productData = JSON.parse(jsonEl.textContent); } catch (e) { /* skip */ }
        }

        // Variant selection
        var selectors = form.querySelectorAll('[data-option-selector]');
        selectors.forEach(function (sel) {
          sel.addEventListener('change', function () { ProductForm.onVariantChange(form, productData); });
        });

        // Form submit — Ajax add to cart
        form.addEventListener('submit', function (e) {
          e.preventDefault();
          var btn = form.querySelector('[data-add-to-cart]');
          if (!btn || btn.disabled) return;

          btn.classList.add('is-loading');
          btn.disabled = true;

          var formData = new FormData(form);
          var body = {};
          formData.forEach(function (v, k) { body[k] = v; });

          fetchJSON('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify({ items: [{ id: body.id, quantity: parseInt(body.quantity, 10) || 1 }] })
          })
            .then(function () {
              btn.classList.remove('is-loading');
              btn.disabled = false;
              CartDrawer.open();
              CartDrawer.refresh();
            })
            .catch(function (err) {
              btn.classList.remove('is-loading');
              btn.disabled = false;
              var notice = form.querySelector('[data-form-notice]');
              if (notice) {
                notice.textContent = err.message || 'Could not add item to cart.';
                notice.hidden = false;
                setTimeout(function () { notice.hidden = true; }, 4000);
              }
            });
        });
      });
    },

    onVariantChange: function (form, productData) {
      if (!productData || !productData.variants) return;
      var selected = [];
      form.querySelectorAll('[data-option-selector]').forEach(function (s) { selected.push(s.value); });

      var variant = productData.variants.find(function (v) {
        return v.options.every(function (opt, i) { return opt === selected[i]; });
      });

      if (!variant) return;

      // Update hidden variant id input
      var idInput = form.querySelector('[name="id"]');
      if (idInput) idInput.value = variant.id;

      // Price
      var priceEl = document.querySelector('[data-product-price]');
      if (priceEl) priceEl.textContent = ProductForm.formatMoney(variant.price);

      var comparePriceEl = document.querySelector('[data-compare-price]');
      if (comparePriceEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          comparePriceEl.textContent = ProductForm.formatMoney(variant.compare_at_price);
          comparePriceEl.hidden = false;
        } else {
          comparePriceEl.textContent = '';
          comparePriceEl.hidden = true;
        }
      }

      // Availability
      var btn = form.querySelector('[data-add-to-cart]');
      var btnText = form.querySelector('[data-add-to-cart-text]');
      if (btn && btnText) {
        if (variant.available) {
          btn.disabled = false;
          btnText.textContent = 'Add to Cart';
        } else {
          btn.disabled = true;
          btnText.textContent = 'Sold Out';
        }
      }

      // SKU
      var skuEl = document.querySelector('[data-product-sku]');
      if (skuEl) skuEl.textContent = variant.sku || '';

      // Featured image
      if (variant.featured_image) {
        var mainImg = document.querySelector('[data-product-main-image]');
        if (mainImg) {
          mainImg.src = variant.featured_image.src;
          mainImg.alt = variant.featured_image.alt || '';
        }
      }

      // Push state
      if (window.history && window.history.replaceState) {
        var url = new URL(window.location);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    },

    formatMoney: function (cents) {
      var amount = (cents / 100).toFixed(2);
      return '$' + amount;
    }
  };

  /* ========================================================================
   * 7. ProductGallery
   * ======================================================================== */

  const ProductGallery = {
    init: function () {
      document.querySelectorAll('[data-product-gallery]').forEach(function (gallery) {
        var mainImage = gallery.querySelector('[data-product-main-image]');
        var thumbnails = gallery.querySelectorAll('[data-gallery-thumb]');
        var modal = gallery.querySelector('[data-gallery-modal]');

        // Thumbnail click
        thumbnails.forEach(function (thumb) {
          thumb.addEventListener('click', function () {
            if (!mainImage) return;
            mainImage.src = thumb.dataset.imageSrc || thumb.src;
            mainImage.alt = thumb.alt || '';
            thumbnails.forEach(function (t) { t.classList.remove('is-active'); });
            thumb.classList.add('is-active');
          });
        });

        // Hover zoom
        if (mainImage) {
          var wrapper = mainImage.parentElement;
          wrapper.addEventListener('mouseenter', function () { mainImage.style.transform = 'scale(1.8)'; });
          wrapper.addEventListener('mouseleave', function () {
            mainImage.style.transform = '';
            mainImage.style.transformOrigin = '';
          });
          wrapper.addEventListener('mousemove', function (e) {
            var rect = wrapper.getBoundingClientRect();
            var x = ((e.clientX - rect.left) / rect.width) * 100;
            var y = ((e.clientY - rect.top) / rect.height) * 100;
            mainImage.style.transformOrigin = x + '% ' + y + '%';
          });

          // Modal zoom on click
          wrapper.addEventListener('click', function () {
            ProductGallery.openModal(mainImage.src, mainImage.alt, modal);
          });
        }

        // Touch swipe for mobile gallery
        var touchStartX = 0;
        var thumbsArray = Array.from(thumbnails);
        gallery.addEventListener('touchstart', function (e) { touchStartX = e.changedTouches[0].clientX; }, { passive: true });
        gallery.addEventListener('touchend', function (e) {
          var dx = e.changedTouches[0].clientX - touchStartX;
          if (Math.abs(dx) < 40) return;
          var currentIdx = thumbsArray.findIndex(function (t) { return t.classList.contains('is-active'); });
          if (currentIdx === -1) currentIdx = 0;
          var nextIdx = dx > 0 ? currentIdx - 1 : currentIdx + 1;
          if (nextIdx >= 0 && nextIdx < thumbsArray.length) thumbsArray[nextIdx].click();
        }, { passive: true });
      });
    },

    openModal: function (src, alt, modal) {
      if (modal) {
        var img = modal.querySelector('img');
        if (img) { img.src = src; img.alt = alt || ''; }
        modal.classList.add('is-open');
        modal.addEventListener('click', function handler() {
          modal.classList.remove('is-open');
          modal.removeEventListener('click', handler);
        });
        return;
      }
      // Fallback: create overlay
      var overlay = document.createElement('div');
      overlay.className = 'gallery-modal is-open';
      overlay.style.cssText = 'position:fixed;inset:0;z-index:9999;background:rgba(0,0,0,.85);display:flex;align-items:center;justify-content:center;cursor:zoom-out;';
      var imgEl = document.createElement('img');
      imgEl.src = src;
      imgEl.alt = alt || '';
      imgEl.style.cssText = 'max-width:90vw;max-height:90vh;object-fit:contain;';
      overlay.appendChild(imgEl);
      document.body.appendChild(overlay);
      overlay.addEventListener('click', function () { overlay.remove(); });
    }
  };

  /* ========================================================================
   * 8. CartDrawer
   * ======================================================================== */

  const CartDrawer = {
    _drawer: null,
    _overlay: null,

    init: function () {
      CartDrawer._drawer = document.querySelector('[data-cart-drawer]');
      CartDrawer._overlay = document.querySelector('[data-cart-drawer-overlay]');

      document.querySelectorAll('[data-cart-drawer-open]').forEach(function (btn) {
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          CartDrawer.open();
          CartDrawer.refresh();
        });
      });

      var closeBtn = CartDrawer._drawer && CartDrawer._drawer.querySelector('[data-cart-drawer-close]');
      if (closeBtn) closeBtn.addEventListener('click', function () { CartDrawer.close(); });
      if (CartDrawer._overlay) CartDrawer._overlay.addEventListener('click', function () { CartDrawer.close(); });

      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && CartDrawer._drawer && CartDrawer._drawer.classList.contains('is-open')) {
          CartDrawer.close();
        }
      });

      // Delegate quantity & remove inside drawer
      if (CartDrawer._drawer) {
        CartDrawer._drawer.addEventListener('click', function (e) {
          var removeBtn = e.target.closest('[data-cart-remove]');
          if (removeBtn) {
            e.preventDefault();
            var key = removeBtn.dataset.cartRemove;
            CartDrawer.changeItem(key, 0);
          }
        });

        CartDrawer._drawer.addEventListener('change', function (e) {
          var input = e.target.closest('[data-cart-quantity-input]');
          if (input) {
            var key = input.dataset.cartQuantityInput;
            CartDrawer.changeItem(key, parseInt(input.value, 10) || 1);
          }
        });

        // Cart note
        var noteField = CartDrawer._drawer.querySelector('[data-cart-note]');
        if (noteField) {
          noteField.addEventListener('change', function () {
            fetch('/cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ note: noteField.value })
            });
          });
        }
      }
    },

    open: function () {
      if (!CartDrawer._drawer) return;
      CartDrawer._drawer.classList.add('is-open');
      if (CartDrawer._overlay) CartDrawer._overlay.classList.add('is-visible');
      document.body.style.overflow = 'hidden';
      trapFocus(CartDrawer._drawer);
    },

    close: function () {
      if (!CartDrawer._drawer) return;
      CartDrawer._drawer.classList.remove('is-open');
      if (CartDrawer._overlay) CartDrawer._overlay.classList.remove('is-visible');
      document.body.style.overflow = '';
      releaseFocus(CartDrawer._drawer);
    },

    refresh: function () {
      fetchJSON('/cart.js')
        .then(function (cart) {
          CartDrawer.updateCount(cart.item_count);
          CartDrawer.renderItems(cart);
        })
        .catch(function () { /* silent */ });
    },

    renderItems: function (cart) {
      var container = CartDrawer._drawer && CartDrawer._drawer.querySelector('[data-cart-drawer-items]');
      if (!container) return;
      if (!cart.items.length) {
        container.innerHTML = '<p class="cart-drawer__empty">Your cart is empty.</p>';
        return;
      }
      var html = '';
      cart.items.forEach(function (item) {
        html += '<div class="cart-drawer__item" data-cart-item="' + item.key + '">';
        html += '<a href="' + item.url + '" class="cart-drawer__item-image">';
        if (item.image) html += '<img src="' + item.image + '" alt="' + item.title + '" width="80" height="80" loading="lazy">';
        html += '</a>';
        html += '<div class="cart-drawer__item-details">';
        html += '<a href="' + item.url + '" class="cart-drawer__item-title">' + item.title + '</a>';
        if (item.variant_title) html += '<p class="cart-drawer__item-variant">' + item.variant_title + '</p>';
        html += '<p class="cart-drawer__item-price">' + ProductForm.formatMoney(item.final_line_price) + '</p>';
        html += '<div class="cart-drawer__item-qty" data-quantity-selector>';
        html += '<button type="button" data-quantity-minus aria-label="Decrease quantity">−</button>';
        html += '<input type="number" value="' + item.quantity + '" min="1" data-cart-quantity-input="' + item.key + '" aria-label="Quantity">';
        html += '<button type="button" data-quantity-plus aria-label="Increase quantity">+</button>';
        html += '</div>';
        html += '<button type="button" data-cart-remove="' + item.key + '" class="cart-drawer__remove" aria-label="Remove ' + item.title + '">Remove</button>';
        html += '</div></div>';
      });
      html += '<div class="cart-drawer__subtotal"><strong>Subtotal:</strong> ' + ProductForm.formatMoney(cart.total_price) + '</div>';
      container.innerHTML = html;
      QuantitySelector.init(); // re-bind quantity buttons inside drawer
    },

    changeItem: function (key, quantity) {
      var container = CartDrawer._drawer && CartDrawer._drawer.querySelector('[data-cart-drawer-items]');
      if (container) container.classList.add('is-loading');

      fetchJSON('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity })
      })
        .then(function (cart) {
          CartDrawer.updateCount(cart.item_count);
          CartDrawer.renderItems(cart);
          if (container) container.classList.remove('is-loading');
        })
        .catch(function () {
          if (container) container.classList.remove('is-loading');
        });
    },

    updateCount: function (count) {
      document.querySelectorAll('[data-cart-count]').forEach(function (el) {
        el.textContent = count;
        el.hidden = count === 0;
      });
    }
  };

  /* ========================================================================
   * 9. CartPage
   * ======================================================================== */

  const CartPage = {
    init: function () {
      var form = document.querySelector('[data-cart-page]');
      if (!form) return;

      form.addEventListener('change', function (e) {
        var input = e.target.closest('[data-cart-page-quantity]');
        if (!input) return;
        var key = input.dataset.cartPageQuantity;
        var quantity = parseInt(input.value, 10) || 1;
        CartPage.update(key, quantity, form);
      });

      form.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-cart-page-remove]');
        if (!btn) return;
        e.preventDefault();
        var key = btn.dataset.cartPageRemove;
        CartPage.update(key, 0, form);
      });
    },

    update: function (key, quantity, form) {
      form.classList.add('is-loading');

      fetchJSON('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity })
      })
        .then(function (cart) {
          CartDrawer.updateCount(cart.item_count);

          // Update line item prices
          cart.items.forEach(function (item) {
            var row = form.querySelector('[data-cart-item="' + item.key + '"]');
            if (!row) return;
            var linePrice = row.querySelector('[data-line-price]');
            if (linePrice) linePrice.textContent = ProductForm.formatMoney(item.final_line_price);
          });

          // Remove lines with quantity 0
          form.querySelectorAll('[data-cart-item]').forEach(function (row) {
            var found = cart.items.some(function (item) { return item.key === row.dataset.cartItem; });
            if (!found) row.remove();
          });

          // Update subtotal
          var subtotal = form.querySelector('[data-cart-subtotal]');
          if (subtotal) subtotal.textContent = ProductForm.formatMoney(cart.total_price);

          if (!cart.items.length) {
            var empty = form.querySelector('[data-cart-empty]');
            if (empty) empty.hidden = false;
          }

          form.classList.remove('is-loading');
        })
        .catch(function () { form.classList.remove('is-loading'); });
    }
  };

  /* ========================================================================
   * 10. Accordion
   * ======================================================================== */

  const Accordion = {
    init: function () {
      document.querySelectorAll('[data-accordion]').forEach(function (container) {
        var allowMultiple = container.dataset.accordionMultiple === 'true';
        var triggers = container.querySelectorAll('[data-accordion-trigger]');

        triggers.forEach(function (trigger) {
          trigger.addEventListener('click', function () {
            var panel = document.getElementById(trigger.getAttribute('aria-controls'));
            if (!panel) return;
            var isOpen = trigger.getAttribute('aria-expanded') === 'true';

            if (!allowMultiple) {
              triggers.forEach(function (t) {
                var p = document.getElementById(t.getAttribute('aria-controls'));
                t.setAttribute('aria-expanded', 'false');
                if (p) p.style.maxHeight = '0px';
              });
            }

            trigger.setAttribute('aria-expanded', String(!isOpen));
            panel.style.maxHeight = isOpen ? '0px' : panel.scrollHeight + 'px';
          });
        });
      });
    }
  };

  /* ========================================================================
   * 11. Tabs
   * ======================================================================== */

  const Tabs = {
    init: function () {
      document.querySelectorAll('[data-tabs]').forEach(function (container) {
        var tabList = container.querySelector('[role="tablist"]');
        var tabs = container.querySelectorAll('[role="tab"]');
        var panels = container.querySelectorAll('[role="tabpanel"]');

        tabs.forEach(function (tab) {
          tab.addEventListener('click', function () {
            tabs.forEach(function (t) { t.setAttribute('aria-selected', 'false'); t.tabIndex = -1; });
            panels.forEach(function (p) { p.hidden = true; });

            tab.setAttribute('aria-selected', 'true');
            tab.tabIndex = 0;
            var panel = document.getElementById(tab.getAttribute('aria-controls'));
            if (panel) panel.hidden = false;
          });

          tab.addEventListener('keydown', function (e) {
            var tabArray = Array.from(tabs);
            var idx = tabArray.indexOf(tab);
            if (e.key === 'ArrowRight') { e.preventDefault(); tabArray[(idx + 1) % tabArray.length].click(); tabArray[(idx + 1) % tabArray.length].focus(); }
            if (e.key === 'ArrowLeft') { e.preventDefault(); tabArray[(idx - 1 + tabArray.length) % tabArray.length].click(); tabArray[(idx - 1 + tabArray.length) % tabArray.length].focus(); }
          });
        });
      });
    }
  };

  /* ========================================================================
   * 12. Newsletter
   * ======================================================================== */

  const Newsletter = {
    init: function () {
      document.querySelectorAll('[data-newsletter-form]').forEach(function (form) {
        form.addEventListener('submit', function (e) {
          e.preventDefault();
          var emailInput = form.querySelector('input[type="email"]');
          var message = form.querySelector('[data-newsletter-message]');
          if (!emailInput) return;

          var email = emailInput.value.trim();
          if (!Newsletter.validateEmail(email)) {
            if (message) { message.textContent = 'Please enter a valid email address.'; message.className = 'newsletter__message newsletter__message--error'; message.hidden = false; }
            return;
          }

          var formData = new FormData(form);
          fetch(form.action || '/contact#contact_form', {
            method: 'POST',
            body: formData,
            headers: { 'Accept': 'application/json' }
          })
            .then(function (res) {
              if (message) {
                if (res.ok || res.status === 200) {
                  message.textContent = 'Thank you for subscribing!';
                  message.className = 'newsletter__message newsletter__message--success';
                  emailInput.value = '';
                } else {
                  message.textContent = 'Something went wrong. Please try again.';
                  message.className = 'newsletter__message newsletter__message--error';
                }
                message.hidden = false;
              }
            })
            .catch(function () {
              if (message) {
                message.textContent = 'Something went wrong. Please try again.';
                message.className = 'newsletter__message newsletter__message--error';
                message.hidden = false;
              }
            });
        });
      });
    },

    validateEmail: function (email) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    }
  };

  /* ========================================================================
   * 13. Carousel / Slider
   * ======================================================================== */

  const Carousel = {
    init: function () {
      document.querySelectorAll('[data-carousel]').forEach(function (carousel) {
        var track = carousel.querySelector('[data-carousel-track]');
        var prevBtn = carousel.querySelector('[data-carousel-prev]');
        var nextBtn = carousel.querySelector('[data-carousel-next]');
        var dotsContainer = carousel.querySelector('[data-carousel-dots]');
        if (!track) return;

        var slides = track.querySelectorAll('[data-carousel-slide]');
        if (!slides.length) return;

        // Dot indicators
        if (dotsContainer) {
          slides.forEach(function (_, i) {
            var dot = document.createElement('button');
            dot.className = 'carousel__dot' + (i === 0 ? ' is-active' : '');
            dot.setAttribute('aria-label', 'Go to slide ' + (i + 1));
            dot.addEventListener('click', function () {
              slides[i].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
            });
            dotsContainer.appendChild(dot);
          });
        }

        function updateControls() {
          var scrollLeft = track.scrollLeft;
          var maxScroll = track.scrollWidth - track.clientWidth;
          if (prevBtn) prevBtn.disabled = scrollLeft <= 2;
          if (nextBtn) nextBtn.disabled = scrollLeft >= maxScroll - 2;

          if (dotsContainer) {
            var dots = dotsContainer.querySelectorAll('.carousel__dot');
            var slideWidth = slides[0].offsetWidth;
            var activeIdx = Math.round(scrollLeft / slideWidth);
            dots.forEach(function (d, i) { d.classList.toggle('is-active', i === activeIdx); });
          }
        }

        track.addEventListener('scroll', debounce(updateControls, 80), { passive: true });

        if (prevBtn) {
          prevBtn.addEventListener('click', function () {
            track.scrollBy({ left: -track.clientWidth, behavior: 'smooth' });
          });
        }
        if (nextBtn) {
          nextBtn.addEventListener('click', function () {
            track.scrollBy({ left: track.clientWidth, behavior: 'smooth' });
          });
        }

        updateControls();
      });
    }
  };

  /* ========================================================================
   * 14. ScrollingMarquee
   * ======================================================================== */

  const ScrollingMarquee = {
    init: function () {
      var prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      document.querySelectorAll('[data-marquee]').forEach(function (marquee) {
        var inner = marquee.querySelector('[data-marquee-inner]');
        if (!inner) return;

        // Duplicate content for seamless loop
        if (!inner.dataset.duplicated) {
          inner.innerHTML += inner.innerHTML;
          inner.dataset.duplicated = 'true';
        }

        if (prefersReduced) {
          inner.style.animationPlayState = 'paused';
          return;
        }

        marquee.addEventListener('mouseenter', function () { inner.style.animationPlayState = 'paused'; });
        marquee.addEventListener('mouseleave', function () { inner.style.animationPlayState = 'running'; });
      });
    }
  };

  /* ========================================================================
   * 15. AddressForm
   * ======================================================================== */

  const AddressForm = {
    init: function () {
      document.querySelectorAll('[data-address-form]').forEach(function (form) {
        var countrySelect = form.querySelector('[data-address-country]');
        var provinceSelect = form.querySelector('[data-address-province]');
        var provinceWrapper = form.querySelector('[data-address-province-wrapper]');

        if (countrySelect && provinceSelect) {
          countrySelect.addEventListener('change', function () {
            var option = countrySelect.options[countrySelect.selectedIndex];
            var provinces = option.dataset.provinces;
            if (provinces) {
              try {
                var parsed = JSON.parse(provinces);
                provinceSelect.innerHTML = '';
                parsed.forEach(function (p) {
                  var opt = document.createElement('option');
                  opt.value = p[0];
                  opt.textContent = p[1];
                  provinceSelect.appendChild(opt);
                });
                if (provinceWrapper) provinceWrapper.hidden = false;
              } catch (e) {
                if (provinceWrapper) provinceWrapper.hidden = true;
              }
            } else {
              if (provinceWrapper) provinceWrapper.hidden = true;
            }
          });

          // Trigger initial state
          countrySelect.dispatchEvent(new Event('change'));
        }
      });

      // Toggle new / edit address forms
      document.querySelectorAll('[data-address-toggle]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var target = document.getElementById(btn.dataset.addressToggle);
          if (target) target.hidden = !target.hidden;
        });
      });
    }
  };

  /* ========================================================================
   * 16. GiftCard
   * ======================================================================== */

  const GiftCard = {
    init: function () {
      var copyBtn = document.querySelector('[data-gift-card-copy]');
      if (copyBtn) {
        copyBtn.addEventListener('click', function () {
          var code = document.querySelector('[data-gift-card-code]');
          if (!code) return;
          navigator.clipboard.writeText(code.textContent.trim()).then(function () {
            var original = copyBtn.textContent;
            copyBtn.textContent = 'Copied!';
            setTimeout(function () { copyBtn.textContent = original; }, 2000);
          });
        });
      }

      var printBtn = document.querySelector('[data-gift-card-print]');
      if (printBtn) {
        printBtn.addEventListener('click', function () { window.print(); });
      }
    }
  };

  /* ========================================================================
   * 17. SmoothScroll
   * ======================================================================== */

  const SmoothScroll = {
    init: function () {
      document.querySelectorAll('a[href^="#"]').forEach(function (link) {
        link.addEventListener('click', function (e) {
          var hash = link.getAttribute('href');
          if (!hash || hash === '#') return;
          var target = document.querySelector(hash);
          if (!target) return;
          e.preventDefault();

          var header = document.querySelector('.section-site-header');
          var offset = header ? header.offsetHeight : 0;
          var top = target.getBoundingClientRect().top + window.scrollY - offset;
          window.scrollTo({ top: top, behavior: 'smooth' });

          if (window.history && window.history.pushState) {
            window.history.pushState(null, '', hash);
          }
        });
      });
    }
  };

  /* ========================================================================
   * 18. ShareButtons
   * ======================================================================== */

  const ShareButtons = {
    init: function () {
      document.querySelectorAll('[data-share-copy]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var url = btn.dataset.shareCopy || window.location.href;
          navigator.clipboard.writeText(url).then(function () {
            var original = btn.textContent;
            btn.textContent = 'Link Copied!';
            setTimeout(function () { btn.textContent = original; }, 2000);
          });
        });
      });
    }
  };

  /* ========================================================================
   * 19. LazyLoad
   * ======================================================================== */

  const LazyLoad = {
    _observer: null,

    init: function () {
      if (!('IntersectionObserver' in window)) {
        // Fallback: load all immediately
        document.querySelectorAll('img[data-src]').forEach(function (img) {
          img.src = img.dataset.src;
          if (img.dataset.srcset) img.srcset = img.dataset.srcset;
        });
        return;
      }

      LazyLoad._observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var img = entry.target;
          if (img.dataset.src) img.src = img.dataset.src;
          if (img.dataset.srcset) img.srcset = img.dataset.srcset;
          img.addEventListener('load', function () { img.classList.add('is-loaded'); }, { once: true });
          LazyLoad._observer.unobserve(img);
        });
      }, { rootMargin: '200px 0px' });

      document.querySelectorAll('img[data-src]').forEach(function (img) {
        LazyLoad._observer.observe(img);
      });
    },

    destroy: function () {
      if (LazyLoad._observer) LazyLoad._observer.disconnect();
    }
  };

  /* ========================================================================
   * Init
   * ======================================================================== */

  function initAll() {
    AnnouncementBar.init();
    StickyHeader.init();
    MobileMenu.init();
    PredictiveSearch.init();
    QuantitySelector.init();
    ProductForm.init();
    ProductGallery.init();
    CartDrawer.init();
    CartPage.init();
    Accordion.init();
    Tabs.init();
    Newsletter.init();
    Carousel.init();
    ScrollingMarquee.init();
    AddressForm.init();
    GiftCard.init();
    SmoothScroll.init();
    ShareButtons.init();
    LazyLoad.init();
  }

  function destroyAll() {
    AnnouncementBar.destroy();
    StickyHeader.destroy();
    MobileMenu.destroy();
    LazyLoad.destroy();
  }

  // Boot
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Shopify section lifecycle events
  document.addEventListener('shopify:section:load', function () {
    destroyAll();
    initAll();
  });

  document.addEventListener('shopify:section:unload', function () {
    destroyAll();
  });
})();
